import React from 'react';

// Types
import { SuitabilityAnalysisResultType } from '../../../../redux/modules/types/suitability-analysis';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import { PILL_COLORS } from '../../../../utils/colors';

// Components
import { PrimaryLink } from '../../buttons';
import { HoldingIcon } from '../../common';
import { PillLabel, WarningLabel } from '../../misc';
import { Tooltip, WarningTooltip } from '../../tooltips';
import DataTable from '../../data/DataTable';
import ExtendedSuitabilityAnalysisPortfolio from '@/types/extended-suitability-analysis-portfolio';
import LocalizedString from '@/types/localized-string';
import ExtendedAnalysisPortfolioItem from '@/types/extended-analysis-portfolio-item';

type Props = {
  portfolios: ExtendedSuitabilityAnalysisPortfolio[];
  onViewDetails: (selectedPortfolio: ExtendedSuitabilityAnalysisPortfolio) => void;
};

type PortfolioNameRow = {
  color: string;
  name: LocalizedString;
  type: COMPARISON_ITEM_TYPES;
};

const PortfoliosTable = ({ portfolios, onViewDetails }: Props) => {
  const generatePortfolioColumns = () => [
    {
      Header: formatMessage('ProductSuitability.PortfoliosTable.PortfolioName'),
      id: 'name',
      accessor: (data: PortfolioNameRow) => ({
        color: data.color,
        name: data.name,
        type: data.type,
      }),
      className: 'portfolio-name',
      width: 250,
      Cell: ({ value }: { value: PortfolioNameRow }) =>
        value ? (
          <span data-private data-dd-privacy="mask">
            <HoldingIcon type={value.type} color={value.color} />
            {formatLocalizedString(value.name)}
          </span>
        ) : null,
    },
    {
      Header: formatMessage('ProductSuitability.PortfoliosTable.Holdings'),
      accessor: 'items',
      className: 'holding',
      Cell: ({ value }: { value: ExtendedAnalysisPortfolioItem[] }) =>
        value ? (
          <span>
            <FormattedMessage id="ProductSuitability.PortfoliosTable.HoldingsCount" values={{ count: value.length }} />
          </span>
        ) : null,
    },
    {
      Header: formatMessage('ProductSuitability.PortfoliosTable.SuitabilityResults'),
      accessor: '',
      className: 'suitability-result',
      width: 210,
      Cell: ({ value }: { value: ExtendedSuitabilityAnalysisPortfolio }) => {
        let pillColor: PILL_COLORS;
        let resultMessage = '';
        // Render warning pill if the portfolio doesn't have a risk profile
        if (!value.riskProfile) {
          pillColor = PILL_COLORS.ORANGE;
          resultMessage = `
              ${formatMessage(`ProductSuitability.SuitabilityResults.${SuitabilityAnalysisResultType.ERROR_RISK_PROFILE_NOT_FOUND}.Prefix`)}
              ${formatMessage(`ProductSuitability.SuitabilityResults.${SuitabilityAnalysisResultType.ERROR_RISK_PROFILE_NOT_FOUND}.Title`)}
            `;
        } else {
          // Render pill based on the portfolio Suitability Analysis result
          switch (value.suitabilityResult) {
            case SuitabilityAnalysisResultType.ERROR_PRODUCT_NOT_FOUND:
            case SuitabilityAnalysisResultType.ERROR_RISK_PROFILE_NOT_FOUND:
              pillColor = PILL_COLORS.ORANGE;
              break;
            case SuitabilityAnalysisResultType.OK:
              pillColor = PILL_COLORS.GREEN;
              break;
            case SuitabilityAnalysisResultType.REVIEW_REQUIRED:
              pillColor = PILL_COLORS.RED;
              break;
            default:
              pillColor = PILL_COLORS.GREY;
              break;
          }

          resultMessage = `
              ${formatMessage(`ProductSuitability.SuitabilityResults.${value.suitabilityResult}.Prefix`)}
              ${formatMessage(`ProductSuitability.SuitabilityResults.${value.suitabilityResult}.Title`)}
            `;
        }

        return (
          <Tooltip delayShow={500} message={resultMessage}>
            <span>
              <PillLabel color={pillColor} text={resultMessage} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      Header: '',
      accessor: '',
      className: 'actions',
      Cell: ({ value }: { value: ExtendedSuitabilityAnalysisPortfolio }) => {
        if (
          value.riskProfile &&
          value.suitabilityResult !== SuitabilityAnalysisResultType.ERROR_PRODUCT_NOT_FOUND &&
          value.suitabilityResult !== SuitabilityAnalysisResultType.ERROR_RISK_PROFILE_NOT_FOUND
        ) {
          return (
            <PrimaryLink onClick={() => onViewDetails(value)}>
              <FormattedMessage id="ProductSuitability.PortfoliosTable.ViewDetails" />
            </PrimaryLink>
          );
        }

        let warningTitle = formatMessage(`ProductSuitability.SuitabilityResults.${SuitabilityAnalysisResultType.ERROR_PRODUCT_NOT_FOUND}.Title`);
        let warningMessage = formatMessage(`ProductSuitability.SuitabilityResults.${SuitabilityAnalysisResultType.ERROR_PRODUCT_NOT_FOUND}.Description`);

        if (!value.riskProfile) {
          warningTitle = formatMessage(`ProductSuitability.SuitabilityResults.${SuitabilityAnalysisResultType.ERROR_RISK_PROFILE_NOT_FOUND}.Title`);
          warningMessage = formatMessage(`ProductSuitability.SuitabilityResults.${SuitabilityAnalysisResultType.ERROR_RISK_PROFILE_NOT_FOUND}.Description`);
        }

        // Render a warning tooltip if the portfolio is missing a risk profile
        // or the portfolio's Suitability result is ERROR_PRODUCT_NOT_FOUND
        return (
          <WarningTooltip title={warningTitle} message={warningMessage}>
            <span>
              <WarningLabel />
            </span>
          </WarningTooltip>
        );
      },
    },
  ];

  return (
    <DataTable
      className="suitability-portfolios"
      columns={generatePortfolioColumns()}
      data={portfolios}
      noDataText={formatMessage('ProductSuitability.PortfoliosTable.NoData')}
    />
  );
};

export default PortfoliosTable;
